<template>
	<App title="Agent Profile">
		<div class="agent-layout">

			<!-- ── Hero ──────────────────────────────────────────────────── -->
			<div class="agent-hero">
				<div class="agent-avatar">{{ initials }}</div>
				<div class="agent-hero-info">
					<h2 class="agent-hero-name">{{ agent.fullname }}</h2>
					<span class="agent-badge"><i class="fas fa-user-tie me-1"></i> Agent</span>
					<div class="agent-hero-contacts">
						<span><i class="fas fa-phone me-1 text-muted"></i>{{ agent.phone }}</span>
						<span v-if="agent.address"><i class="fas fa-map-marker-alt me-1 text-muted"></i>{{ agent.address.full_address }}</span>
					</div>
				</div>
				<div class="agent-hero-actions">
					<a :href="route('agent.page.group')" class="btn btn-sm btn-outline-secondary">
						<i class="fas fa-arrow-left me-1"></i> Back
					</a>
				</div>
			</div>

			<!-- ── Verification Stats ─────────────────────────────────────── -->
			<div class="agent-stats">
				<div class="agent-stat">
					<div class="agent-stat-icon" :class="agent.isEmailVerified ? 'verified' : 'unverified'">
						<i class="fas fa-envelope"></i>
					</div>
					<div>
						<div class="agent-stat-label">Email</div>
						<div class="agent-stat-value" :class="agent.isEmailVerified ? 'text-success' : 'text-danger'">
							{{ agent.isEmailVerified ? 'Verified' : 'Unverified' }}
						</div>
					</div>
				</div>
				<div class="agent-stat">
					<div class="agent-stat-icon" :class="agent.isBVNVerified ? 'verified' : 'unverified'">
						<i class="fas fa-id-card"></i>
					</div>
					<div>
						<div class="agent-stat-label">BVN</div>
						<div class="agent-stat-value" :class="agent.isBVNVerified ? 'text-success' : 'text-danger'">
							{{ agent.isBVNVerified ? 'Verified' : 'Unverified' }}
						</div>
					</div>
				</div>
				<div class="agent-stat">
					<div class="agent-stat-icon" :class="agent.isGIDVerified ? 'verified' : 'unverified'">
						<i class="fas fa-fingerprint"></i>
					</div>
					<div>
						<div class="agent-stat-label">GID</div>
						<div class="agent-stat-value" :class="agent.isGIDVerified ? 'text-success' : 'text-danger'">
							{{ agent.isGIDVerified ? 'Verified' : 'Unverified' }}
						</div>
					</div>
				</div>
				<div class="agent-stat">
					<div class="agent-stat-icon" :class="agent.isEmploymentVerified ? 'verified' : 'unverified'">
						<i class="fas fa-briefcase"></i>
					</div>
					<div>
						<div class="agent-stat-label">Credit</div>
						<div class="agent-stat-value" :class="agent.isEmploymentVerified ? 'text-success' : 'text-danger'">
							{{ agent.isEmploymentVerified ? 'Verified' : 'Unverified' }}
						</div>
					</div>
				</div>
			</div>

			<!-- ── Agent Information ──────────────────────────────────────── -->
			<div class="agent-card">
				<div class="agent-card-header">
					<h3 class="agent-card-title">Agent Information</h3>
					<span class="agent-id-badge">#{{ agent.id }}</span>
				</div>
				<div class="agent-card-body">
					<div class="agent-field">
						<p class="agent-field-label">Full Name</p>
						<p class="agent-field-value">{{ agent.fullname || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Phone Number</p>
						<p class="agent-field-value">{{ agent.phone || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Date of Birth</p>
						<p class="agent-field-value">{{ $render.date(agent.birthday_at) || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Gender</p>
						<p class="agent-field-value">{{ $render.enum(agent.gender) || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">BVN</p>
						<p class="agent-field-value">{{ agent.bvn || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">GID</p>
						<p class="agent-field-value">{{ agent.gid || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">GID Type</p>
						<p class="agent-field-value">{{ gid_title || '—' }}</p>
					</div>
					<div v-if="agent.address" class="agent-field">
						<p class="agent-field-label">Address</p>
						<p class="agent-field-value">{{ agent.address.full_address }}</p>
					</div>
				</div>
			</div>

			<!-- ── Employment ─────────────────────────────────────────────── -->
			<div v-if="agent.employment" class="agent-card">
				<div class="agent-card-header">
					<h3 class="agent-card-title"><i class="fas fa-briefcase me-2 text-muted"></i>Employment</h3>
				</div>
				<div class="agent-card-body">
					<div class="agent-field">
						<p class="agent-field-label">Employment Type</p>
						<p class="agent-field-value">{{ agent.employment.type || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Company Name</p>
						<p class="agent-field-value">{{ agent.employment.name || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Company Phone</p>
						<p class="agent-field-value">{{ agent.employment.phone || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Company Email</p>
						<p class="agent-field-value">{{ agent.employment.email || '—' }}</p>
					</div>
					<div v-if="agent.employment.company" class="agent-field">
						<p class="agent-field-label">Company Type</p>
						<p class="agent-field-value">{{ agent.employment.company }}</p>
					</div>
					<div v-if="agent.employment.number" class="agent-field">
						<p class="agent-field-label">CAC Number</p>
						<p class="agent-field-value">{{ agent.employment.number }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Role</p>
						<p class="agent-field-value">{{ agent.employment.role || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Annual Salary</p>
						<p class="agent-field-value">{{ agent.employment.salary.formatted }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Company Address</p>
						<p class="agent-field-value">{{ agent.employment.address?.full_address || '—' }}</p>
					</div>
				</div>
			</div>

			<!-- ── Next of Kin ────────────────────────────────────────────── -->
			<div v-if="agent.kin" class="agent-card">
				<div class="agent-card-header">
					<h3 class="agent-card-title"><i class="fas fa-users me-2 text-muted"></i>Next of Kin</h3>
				</div>
				<div class="agent-card-body">
					<div class="agent-field">
						<p class="agent-field-label">Name</p>
						<p class="agent-field-value">{{ agent.kin.name || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Email</p>
						<p class="agent-field-value">{{ agent.kin.email || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Phone</p>
						<p class="agent-field-value">{{ agent.kin.phone || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Relationship</p>
						<p class="agent-field-value">{{ agent.kin.relationship || '—' }}</p>
					</div>
					<div v-if="agent.kin.address" class="agent-field">
						<p class="agent-field-label">Address</p>
						<p class="agent-field-value">{{ agent.kin.address.full_address }}</p>
					</div>
				</div>
			</div>

			<!-- ── Wallet ─────────────────────────────────────────────────── -->
			<div v-if="agent.wallet" class="agent-card">
				<div class="agent-card-header">
					<h3 class="agent-card-title"><i class="fas fa-wallet me-2 text-muted"></i>Wallet</h3>
				</div>
				<div class="agent-card-body">
					<div class="agent-field">
						<p class="agent-field-label">Bank</p>
						<p class="agent-field-value">{{ agent.wallet.bank || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Account Number</p>
						<p class="agent-field-value">{{ agent.wallet.number || '—' }}</p>
					</div>
					<div class="agent-field">
						<p class="agent-field-label">Balance</p>
						<p class="agent-field-value">{{ $render.money(agent.wallet.balance) }}</p>
					</div>
				</div>
			</div>

			<!-- ── BVN Verification ───────────────────────────────────────── -->
			<div v-if="!agent.isBVNVerified" class="agent-section">
				<div class="agent-section-header">
					<div>
						<div class="agent-section-title"><i class="fas fa-id-card me-2 text-muted"></i>BVN Verification</div>
						<div class="agent-section-desc">Enter the agent's BVN to complete verification</div>
					</div>
				</div>
				<div class="agent-section-body">
					<Form ref="form" @submit="onSubmitBVN">
						<div class="row">
							<div class="col-md-6">
								<InputField id="bvn" v-model="bvn" name="bvn" rules="required|length:11|numeric" label="BVN" placeholder="Enter BVN" />
							</div>
						</div>
						<div class="agent-section-footer">
							<button type="reset" class="btn btn-sm btn-outline-secondary">Reset</button>
							<Button id="action" type="submit" :label="labels" :loading="loading.bvn" :done="done.bvn" class="btn btn-sm btn-primary" />
						</div>
					</Form>
				</div>
			</div>

			<!-- ── GID Verification ───────────────────────────────────────── -->
			<div v-if="!agent.isGIDVerified" class="agent-section">
				<div class="agent-section-header">
					<div>
						<div class="agent-section-title"><i class="fas fa-fingerprint me-2 text-muted"></i>GID Verification</div>
						<div class="agent-section-desc">Enter the agent's government ID to complete verification</div>
					</div>
				</div>
				<div class="agent-section-body">
					<Form ref="form" @submit="onSubmitGID">
						<div class="row">
							<div class="col-md-6">
								<InputField id="gid" v-model="gid" name="gid" rules="required" label="GID" placeholder="Enter GID" />
							</div>
							<div class="col-md-6">
								<SelectField id="gid_type" v-model="gid_type" name="gid_type" rules="required" label="GID Type" placeholder="GID Type" :options="options">
									<option v-for="gt in gid_types" :key="gt.value" :value="gt.value">
										{{ gt.name }}
									</option>
								</SelectField>
							</div>
						</div>
						<div class="agent-section-footer">
							<button type="reset" class="btn btn-sm btn-outline-secondary">Reset</button>
							<Button id="action" type="submit" :label="labels" :loading="loading.gid" :done="done.gid" class="btn btn-sm btn-primary" />
						</div>
					</Form>
				</div>
			</div>

		</div>
	</App>
</template>

<script lang="ts">
import type Agent from '@/models/Agent'
import { GIDTitle } from '@/models/Agent'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { AxiosError } from 'axios'
import { Options as Select2Options } from 'select2'
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'

@Component({
	components: {
		App,
		Button,
		InputField,
		Form,
		SelectField,
	},
})
class Single extends Vue {
	loading = {
		bvn: false,
		gid: false,
	}
	done = {
		bvn: false,
		gid: false,
	}

	bvn = ''
	gid = ''
	gid_type = ''

	@Prop({ type: Object })
	readonly agent!: Agent

	get initials() {
		return (this.agent?.fullname ?? '')
			.split(' ')
			.slice(0, 2)
			.map((w) => w[0]?.toUpperCase() ?? '')
			.join('')
	}

	get gid_title() {
		return GIDTitle(this.agent.gid_type)
	}

	get labels(): ButtonLabel {
		return {
			normal: 'Update',
			loading: 'Updating',
			done: 'Updated',
		}
	}

	get options(): Select2Options {
		return {
			placeholder: 'Select GID Type',
			minimumResultsForSearch: Infinity,
			//@ts-ignore
			ajax: null,
		}
	}

	get gid_types() {
		return [
			{ name: 'National ID', value: 'nin' },
			{ name: 'International Passport', value: 'ipn' },
			{ name: 'Drivers License', value: 'dln' },
			{ name: 'Voters Card', value: 'vin' },
		]
	}

	onSubmitBVN(payload) {
		this.loading.bvn = true
		this.$sweet
			.fire({
				title: 'Are You Sure?',
				text: "You won't be able to revert this!",
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#3085d6',
				cancelButtonColor: '#d33',
				confirmButtonText: 'YES, CLEAR IT!',
			})
			.then((result) => {
				if (result.isConfirmed) {
					this.$http
						.patch(this.route('agent.ajax.update.bvn', { agent: this.agent.slug }), payload)
						.then(() => (this.done.bvn = true))
						.catch((e: AxiosError) => this.$error(e))
						.finally(() => (this.loading.bvn = false))
				} else {
					this.loading.bvn = false
				}
			})
	}

	onSubmitGID(payload) {
		this.loading.gid = true
		this.$sweet
			.fire({
				title: 'Are You Sure?',
				text: "You won't be able to revert this!",
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#3085d6',
				cancelButtonColor: '#d33',
				confirmButtonText: 'YES, CLEAR IT!',
			})
			.then((result) => {
				if (result.isConfirmed) {
					this.$http
						.patch(this.route('agent.ajax.update.gid', { agent: this.agent.slug }), payload)
						.then(() => (this.done.gid = true))
						.catch((e: AxiosError) => this.$error(e))
						.finally(() => (this.loading.gid = false))
				} else {
					this.loading.gid = false
				}
			})
	}
}
export default toNative(Single)
</script>

<style scoped lang="scss">
.agent-layout {
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

// ─── Hero ──────────────────────────────────────────────────────────────────
.agent-hero {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	background: #fff;
	border-radius: 12px;
	padding: 1.75rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	flex-wrap: wrap;
}

.agent-avatar {
	flex-shrink: 0;
	width: 72px;
	height: 72px;
	border-radius: 50%;
	background: linear-gradient(135deg, #1e3a2f, #3a7d52);
	color: #fff;
	font-size: 1.5rem;
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
	letter-spacing: 0.02em;
	box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.agent-hero-info {
	flex: 1;
	min-width: 0;
}

.agent-hero-name {
	font-size: 1.25rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.3rem;
	line-height: 1.2;
}

.agent-badge {
	display: inline-block;
	padding: 0.2rem 0.7rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	background: #dcfce7;
	color: #15803d;
	margin-bottom: 0.5rem;
}

.agent-hero-contacts {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	font-size: 0.82rem;
	color: #374151;
	margin-top: 0.25rem;
}

.agent-hero-actions {
	flex-shrink: 0;
	margin-left: auto;
	display: flex;
	gap: 0.5rem;
	align-items: flex-start;
}

// ─── Stats ─────────────────────────────────────────────────────────────────
.agent-stats {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 0.875rem;

	@media (max-width: 640px) {
		grid-template-columns: repeat(2, 1fr);
	}
}

.agent-stat {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	background: #fff;
	border-radius: 10px;
	padding: 0.875rem 1rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.agent-stat-icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.85rem;

	&.verified {
		background: #f0fdf4;
		color: #16a34a;
	}

	&.unverified {
		background: #fef2f2;
		color: #dc2626;
	}
}

.agent-stat-label {
	font-size: 0.68rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #9ca3af;
	margin-bottom: 0.1rem;
}

.agent-stat-value {
	font-size: 0.85rem;
	font-weight: 600;
	color: #111827;
}

// ─── Card ──────────────────────────────────────────────────────────────────
.agent-card {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	overflow: hidden;
}

.agent-card-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1.1rem 1.5rem;
	border-bottom: 1px solid #f1f3f5;
}

.agent-card-title {
	font-size: 1rem;
	font-weight: 700;
	color: #111827;
	margin: 0;
}

.agent-id-badge {
	font-size: 0.8rem;
	font-weight: 600;
	color: #6b7280;
	background: #f3f4f6;
	padding: 0.2rem 0.6rem;
	border-radius: 6px;
}

.agent-card-body {
	display: grid;
	grid-template-columns: repeat(3, 1fr);

	@media (max-width: 768px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (max-width: 480px) {
		grid-template-columns: 1fr;
	}
}

// ─── Fields ────────────────────────────────────────────────────────────────
.agent-field {
	padding: 1.25rem 1.5rem;
	border-right: 1px solid #f1f3f5;
	border-bottom: 1px solid #f1f3f5;

	&:nth-child(3n) {
		border-right: none;
	}
}

.agent-field-label {
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #9ca3af;
	margin-bottom: 0.35rem;
}

.agent-field-value {
	font-size: 0.95rem;
	font-weight: 500;
	color: #111827;
	margin: 0;
	word-break: break-word;
}

// ─── Section (forms) ───────────────────────────────────────────────────────
.agent-section {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	overflow: hidden;
}

.agent-section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding: 1rem 1.5rem;
	background: #f9fafb;
	border-bottom: 1px solid #f1f3f5;
}

.agent-section-title {
	font-size: 0.9rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.2rem;
}

.agent-section-desc {
	font-size: 0.75rem;
	color: #6b7280;
}

.agent-section-body {
	padding: 1.5rem;
}

.agent-section-footer {
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
	margin-top: 1.25rem;
	padding-top: 1rem;
	border-top: 1px solid #f1f3f5;
}
</style>
